import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import {
  type ApiOutputs,
  accountPreferencesUpdateSchema,
  accountProfileUpdateSchema,
  ownAccountSchema,
} from "@rosc/contracts";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { referenceClient } from "@/lib/orpc/client";
import {
  getVisitScope,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";
import { useAuthStore } from "@/store/auth.store";

const orpc = createTanstackQueryUtils(referenceClient);
type WriteState = { scope: VisitScope; phase: "pending" | "success" | "error" };
export function useAccountSettings() {
  const scope = useVisitScope();
  const context = { expectedUserUid: scope?.uid };
  const profile = useMutation(orpc.updateMe.mutationOptions({ context }));
  const preferences = useMutation(
    orpc.updateMyPreferences.mutationOptions({ context }),
  );
  const [state, setState] = useState<WriteState | null>(null);
  const sequence = useRef(0);
  const run = async (write: () => Promise<ApiOutputs["me"]>): Promise<void> => {
    if (!scope || getVisitScope() !== scope) return;
    const attempt = ++sequence.current;
    const current = () =>
      getVisitScope() === scope && sequence.current === attempt;
    setState({ scope, phase: "pending" });
    try {
      const account = ownAccountSchema.parse(await write());
      if (!current()) return;
      if (account.public_id !== scope.accountPublicId)
        throw Error("Account changed");
      setState({ scope, phase: "success" });
    } catch {
      if (current()) setState({ scope, phase: "error" });
    }
  };
  const refreshAccount = useCallback(async (): Promise<void> => {
    if (
      scope &&
      getVisitScope() === scope &&
      state?.scope === scope &&
      state.phase === "success"
    ) {
      await useAuthStore.getState().getUser();
    }
  }, [scope, state]);
  const phase = state?.scope === scope ? state.phase : undefined;
  return {
    refreshAccount,
    updateProfile: (body: unknown): Promise<void> =>
      run(() =>
        profile.mutateAsync({ body: accountProfileUpdateSchema.parse(body) }),
      ),
    updatePreferences: (body: unknown): Promise<void> =>
      run(() =>
        preferences.mutateAsync({
          body: accountPreferencesUpdateSchema.parse(body),
        }),
      ),
    loading: phase === "pending",
    result: phase === "success" ? true : phase === "error" ? false : null,
    error: phase === "error",
  };
}
